/**
 * ppo-cart-reorder-guard.js  –  PPO (Promotional Products Online)
 *
 * Cart-level reorder detection and routing.
 *
 * Four detection paths:
 *   1. Quantity change on tiered-pricing items  -> redirect to PDP modal
 *   2. reorder-via-form items (needs_reorder_form) -> "Request Reorder" banner
 *   3. Stale reorder items (tiered, no widget props) -> inline reorder banner
 *   4. Buy Again failure from new customer accounts -> fallback notice
 *
 * NOTE: PPO's Concept theme uses class "quantity__input" (not "qty-input__input")
 *       and "cart__item-title" (not "cart-item__title").
 */
(function () {
  'use strict';

  // ── Brand Colors ───────────────────────────────────────────────────
  var ACCENT = '#676BA2';
  var ACCENT_BORDER = '#555994';
  var ACCENT_BG = '#EEEEF5';
  var ACCENT_DARK = '#4A4D7C';

  // ── Helper: find Mold line-item keys in the cart ──────────────────
  // Mold items (e.g. "Custom Mold Fee") are added alongside the main
  // product.  When we remove a main item on reorder we should also
  // remove any Mold rows so they don't get orphaned.
  function getMoldLineItemKeys() {
    var keys = [];
    document.querySelectorAll('tr.cart-item').forEach(function (row) {
      var title = (row.getAttribute('data-product-title') || '').toLowerCase();
      if (title.indexOf('mold') >= 0) {
        var k = row.getAttribute('data-line-item-key');
        if (k) keys.push(k);
      }
    });
    return keys;
  }

  // Remove a list of line-item keys sequentially, then call callback
  function removeLineItems(keys, callback) {
    if (!keys.length) { callback(); return; }
    var key = keys.shift();
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(function () { removeLineItems(keys, callback); })
    .catch(function () { removeLineItems(keys, callback); });
  }

  function showRedirectBanner(productUrl) {
    var prev = document.getElementById('ppo-reorder-overlay');
    if (prev) prev.remove();
    var prevModal = document.getElementById('ppo-reorder-modal');
    if (prevModal) prevModal.remove();

    var overlay = document.createElement('div');
    overlay.id = 'ppo-reorder-overlay';
    overlay.style.cssText = [
      'position:fixed;top:0;left:0;width:100%;height:100%;',
      'background:rgba(0,0,0,0.55);z-index:999998;'
    ].join('');

    var modal = document.createElement('div');
    modal.id = 'ppo-reorder-modal';
    modal.style.cssText = [
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);',
      'z-index:999999;background:#fff;',
      'width:360px;max-width:90vw;',
      'padding:36px 32px 28px;border-radius:10px;',
      'box-shadow:0 8px 32px rgba(0,0,0,0.22);',
      'font-family:inherit;text-align:center;'
    ].join('');

    modal.innerHTML =
      '<p style="font-size:18px;font-weight:700;margin:0 0 12px;color:#1d1d1d;">' +
        'Quantity Changed' +
      '</p>' +
      '<p style="font-size:14px;color:#444;margin:0 0 24px;line-height:1.6;">' +
        'Our pricing varies by quantity. Please visit the product page so we can calculate the correct price for you.' +
      '</p>' +
      '<a href="' + productUrl + '" ' +
        'style="display:block;background:' + ACCENT + ';color:#fff;text-decoration:none;' +
               'font-weight:700;font-size:15px;padding:13px 20px;border-radius:6px;' +
               'margin-bottom:12px;cursor:pointer;">' +
        'Go to Product Page' +
      '</a>' +
      '<button id="ppo-reorder-cancel" ' +
        'style="display:block;width:100%;background:#fff;border:2px solid ' + ACCENT + ';' +
               'color:' + ACCENT + ';font-weight:700;font-size:15px;padding:11px 20px;' +
               'border-radius:6px;cursor:pointer;">' +
        'Stay in Cart' +
      '</button>';

    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    function dismiss() {
      overlay.remove();
      modal.remove();
    }

    document.getElementById('ppo-reorder-cancel').addEventListener('click', dismiss);
    overlay.addEventListener('click', dismiss);
  }

  // -------------------------------------------------------------------
  function detectOriginalOrderRef() {
    try {
      var stashed = sessionStorage.getItem('ppo_last_viewed_order_name');
      if (stashed) return stashed;
    } catch (e) {}

    var ref = document.referrer || '';
    if (!ref) return '';
    var match = ref.match(/\/account\/orders\/([^\/\?#]+)/i);
    if (match) {
      return '#' + match[1].substring(0, 12);
    }
    return '';
  }

  // -------------------------------------------------------------------
  // Handle reorder-via-form items
  // -------------------------------------------------------------------
  function handleReorderFormItems() {
    var formRows = document.querySelectorAll('tr[data-needs-reorder-form="true"]');
    if (!formRows.length) return;

    var orderRef = detectOriginalOrderRef();

    var itemSummaries = [];
    formRows.forEach(function (row) {
      var title = row.dataset.productTitle || '';
      var variant = row.dataset.variantTitle || '';
      var sku = row.dataset.productSku || '';
      var qtyInput = row.querySelector('.quantity__input');
      var qty = qtyInput ? qtyInput.value : '?';
      var summary = title;
      if (variant && variant !== 'Default Title') summary += ' (' + variant + ')';
      if (sku) summary += ' [' + sku + ']';
      summary += ' x ' + qty;
      itemSummaries.push(summary);
    });

    var itemsParam = encodeURIComponent(itemSummaries.join(' | '));
    var orderParam = orderRef ? encodeURIComponent(orderRef) : '';
    var formUrl = '/pages/reordering?order=' + orderParam + '&items=' + itemsParam;

    formRows.forEach(function (row) {
      if (row.querySelector('.ppo-reorder-form-banner')) return;

      var lineItemKey = row.dataset.lineItemKey;

      // Dim price + disable qty controls
      var priceEls = row.querySelectorAll('.price__regular, .price__sale');
      priceEls.forEach(function (el) { el.style.opacity = '0.35'; });
      var qtyInput = row.querySelector('.quantity__input');
      if (qtyInput) qtyInput.disabled = true;
      var qtyBtns = row.querySelectorAll('.quantity__button');
      qtyBtns.forEach(function (btn) { btn.disabled = true; });

      var banner = document.createElement('div');
      banner.className = 'ppo-reorder-form-banner';
      banner.style.cssText = [
        'margin:12px 0 4px;padding:16px 18px;',
        'border:2px solid ' + ACCENT + ';background:' + ACCENT_BG + ';',
        'border-radius:10px;text-align:center;'
      ].join('');

      var heading = document.createElement('div');
      heading.style.cssText = 'font-size:15px;font-weight:700;color:#000;margin-bottom:6px;';
      heading.textContent = '\uD83D\uDCDD Reorder Request Needed';

      var message = document.createElement('div');
      message.style.cssText = 'font-size:13px;color:#444;line-height:1.5;margin-bottom:14px;';
      message.textContent = 'This item needs to be processed as a manual reorder so we can match your original specifications exactly. Click below to submit a quick reorder request \u2014 we will confirm everything with a digital proof before invoicing.';

      banner.appendChild(heading);
      banner.appendChild(message);

      var link = document.createElement('a');
      link.href = formUrl;
      link.style.cssText = [
        'display:inline-block;background:' + ACCENT + ';color:#fff;text-decoration:none;',
        'font-weight:700;font-size:14px;padding:11px 28px;cursor:pointer;',
        'border-radius:30px;'
      ].join('');
      link.textContent = 'Request Reorder';

      link.addEventListener('click', function (e) {
        e.preventDefault();
        link.textContent = '\u23F3 Clearing cart\u2026';
        link.style.pointerEvents = 'none';

        // Collect all keys to remove: the main item + any Mold items
        var keysToRemove = getMoldLineItemKeys();
        if (lineItemKey) keysToRemove.unshift(lineItemKey);

        if (keysToRemove.length) {
          removeLineItems(keysToRemove, function () {
            window.location.href = formUrl;
          });
        } else {
          window.location.href = formUrl;
        }
      });

      banner.appendChild(link);

      // Insert into the details cell (Concept theme structure)
      var detailsCell = row.querySelector('.cart-item__details') || row.querySelector('td:first-child');
      if (detailsCell) {
        detailsCell.appendChild(banner);
      }
    });

    disableCheckoutButtons();
  }

  // -------------------------------------------------------------------
  function disableCheckoutButtons() {
    function isReorderFormPresent() {
      return !!document.querySelector('tr[data-needs-reorder-form="true"]');
    }

    function applyDisable() {
      if (!isReorderFormPresent()) return;

      // Concept theme checkout buttons
      var mainBtns = document.querySelectorAll(
        'button[name="checkout"], input[name="checkout"], .cart__checkout-button, ' +
        'button.button--primary[name="checkout"], .cart__ctas button[type="submit"]'
      );
      mainBtns.forEach(function (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.style.opacity = '0.45';
        btn.style.cursor = 'not-allowed';
        btn.style.pointerEvents = 'none';
        btn.setAttribute('title', 'Please use "Request Reorder" above to submit your reorder.');
      });

      var dynamicContainers = document.querySelectorAll(
        '.dynamic-cart-btns, .additional-checkout-buttons, .dynamic-checkout__buttons, ' +
        '[data-shopify="dynamic-checkout-cart"], .shopify-payment-button, .cart__dynamic-checkout-buttons'
      );
      dynamicContainers.forEach(function (el) {
        el.style.opacity = '0.35';
        el.style.pointerEvents = 'none';
        el.style.filter = 'grayscale(60%)';
      });

      if (!document.getElementById('ppo-reorder-checkout-notice')) {
        var target = document.querySelector(
          '.cart__ctas, .cart__footer, .cart-summary, .cart__summary, .cart__blocks'
        );
        if (target) {
          var notice = document.createElement('div');
          notice.id = 'ppo-reorder-checkout-notice';
          notice.style.cssText = [
            'background:' + ACCENT_BG + ';border:none;',
            'padding:12px 16px;margin-bottom:14px;',
            'font-size:12px;color:#000;line-height:1.5;',
            'text-align:center;border-radius:8px;'
          ].join('');
          notice.innerHTML =
            '<strong>\u26A0 Checkout disabled:</strong> One or more items in your cart need to be reordered manually. Please click <strong>Request Reorder</strong> on the item above to submit your request.';
          target.insertBefore(notice, target.firstChild);
        }
      }
    }

    document.addEventListener('click', function (e) {
      if (!isReorderFormPresent()) return;
      var target = e.target;
      if (!target || !target.closest) return;

      var checkoutHit = target.closest(
        'button[name="checkout"], input[name="checkout"], ' +
        '.cart__checkout-button, .dynamic-cart-btns, .additional-checkout-buttons, ' +
        '.dynamic-checkout__buttons, .shopify-payment-button, .cart__dynamic-checkout-buttons'
      );

      if (checkoutHit) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        var banner = document.querySelector('.ppo-reorder-form-banner');
        if (banner) {
          banner.style.transition = 'box-shadow 0.3s';
          banner.style.boxShadow = '0 0 0 4px rgba(103,107,162,0.45)';
          banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function () { banner.style.boxShadow = ''; }, 1200);
        }
      }
    }, true);

    document.addEventListener('submit', function (e) {
      if (!isReorderFormPresent()) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (form.querySelector('button[name="checkout"], input[name="checkout"]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);

    applyDisable();

    var attempts = 0;
    var retry = setInterval(function () {
      applyDisable();
      attempts++;
      if (attempts >= 15) clearInterval(retry);
    }, 400);

    if (window.MutationObserver) {
      var observer = new MutationObserver(function () { applyDisable(); });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { observer.disconnect(); }, 15000);
    }
  }

  // -------------------------------------------------------------------
  // Handle stale reorders
  // -------------------------------------------------------------------
  function handleStaleReorders() {
    var staleRows = document.querySelectorAll('tr[data-stale-reorder="true"]');
    if (!staleRows.length) return;

    var orderRef = detectOriginalOrderRef();

    staleRows.forEach(function (row, idx) {
      if (row.querySelector('.ppo-stale-reorder-banner')) return;

      var titleLink = row.querySelector('a.cart__item-title[href], a[href*="/products/"]');
      var productUrl = titleLink ? titleLink.href : '';
      var lineItemKey = row.dataset.lineItemKey;

      var priceEls = row.querySelectorAll('.price__regular, .price__sale');
      priceEls.forEach(function (el) { el.style.opacity = '0.35'; });

      var qtyInput = row.querySelector('.quantity__input');
      if (qtyInput) qtyInput.disabled = true;
      var qtyBtns = row.querySelectorAll('.quantity__button');
      qtyBtns.forEach(function (btn) { btn.disabled = true; });

      var banner = document.createElement('div');
      banner.className = 'ppo-stale-reorder-banner';
      banner.style.cssText = [
        'margin:12px 0 4px;padding:16px 18px;',
        'border:2px solid ' + ACCENT + ';background:' + ACCENT_BG + ';',
        'text-align:left;'
      ].join('');

      var heading = document.createElement('div');
      heading.style.cssText = 'font-size:15px;font-weight:700;color:' + ACCENT_DARK + ';margin-bottom:6px;text-align:center;';
      heading.textContent = '\uD83D\uDD04 Reorder' + (orderRef ? ' from Order ' + orderRef : '');

      var priceWarning = document.createElement('div');
      priceWarning.style.cssText = 'font-size:12px;color:' + ACCENT_DARK + ';font-weight:600;margin-bottom:10px;text-align:center;';
      priceWarning.textContent = '\u26A0 Price shown is the base price and may not reflect your quantity discount.';

      var message = document.createElement('div');
      message.style.cssText = 'font-size:13px;color:#444;line-height:1.5;margin-bottom:12px;text-align:center;';
      message.textContent = 'We have your original design on file. Visit the product page to confirm your quantity and get the correct price.';

      banner.appendChild(heading);
      banner.appendChild(priceWarning);
      banner.appendChild(message);

      var notesLabel = document.createElement('label');
      notesLabel.style.cssText = 'display:block;font-size:13px;font-weight:700;color:#1d1d1d;margin-bottom:6px;';
      notesLabel.textContent = 'Any changes to your design? (leave blank for an exact reorder)';
      var notesId = 'ppo-reorder-notes-' + idx;
      notesLabel.setAttribute('for', notesId);

      var notesInput = document.createElement('textarea');
      notesInput.id = notesId;
      notesInput.rows = 3;
      notesInput.placeholder = 'e.g. Change text, update quantity, swap colors, etc.';
      notesInput.style.cssText = [
        'width:100%;box-sizing:border-box;',
        'border:1px solid ' + ACCENT + ';padding:10px 12px;',
        'font-family:inherit;font-size:13px;color:#1d1d1d;',
        'margin-bottom:14px;resize:vertical;border-radius:6px;'
      ].join('');

      banner.appendChild(notesLabel);
      banner.appendChild(notesInput);

      var btnWrap = document.createElement('div');
      btnWrap.style.cssText = 'text-align:center;';

      if (productUrl) {
        var link = document.createElement('a');
        link.href = productUrl;
        link.style.cssText = [
          'display:inline-block;background:' + ACCENT + ';color:#fff;text-decoration:none;',
          'font-weight:700;font-size:14px;padding:11px 28px;cursor:pointer;border-radius:6px;'
        ].join('');
        link.textContent = 'Go to Product Page';

        link.addEventListener('click', function (e) {
          e.preventDefault();

          var reorderData = {
            isReorder: true,
            orderRef: orderRef,
            changeNotes: notesInput.value || '',
            timestamp: Date.now()
          };
          try {
            sessionStorage.setItem('ppo_reorder_data', JSON.stringify(reorderData));
          } catch (err) {}

          link.textContent = '\u23F3 Removing old item\u2026';
          link.style.pointerEvents = 'none';

          // Remove main item + any Mold items
          var keysToRemove = getMoldLineItemKeys();
          if (lineItemKey) keysToRemove.unshift(lineItemKey);

          if (keysToRemove.length) {
            removeLineItems(keysToRemove, function () {
              window.location.href = productUrl;
            });
          } else {
            window.location.href = productUrl;
          }
        });

        btnWrap.appendChild(link);
      }

      banner.appendChild(btnWrap);

      var detailsCell = row.querySelector('.cart-item__details') || row.querySelector('td:first-child');
      if (detailsCell) {
        detailsCell.appendChild(banner);
      }
    });
  }

  // -------------------------------------------------------------------
  // Handle Buy Again failures
  // -------------------------------------------------------------------
  function handleBuyAgainFailure() {
    if (!/\/cart(\?|$|\/$)/.test(window.location.pathname + window.location.search)) {
      if (window.location.pathname !== '/cart') return;
    }

    if (document.getElementById('ppo-buy-again-fallback-notice')) return;

    // Detect empty cart — the Concept theme adds .is-empty to .cart when empty
    var cartIsEmpty = !!document.querySelector('.cart.is-empty');
    var cartRows = document.querySelectorAll('tr.cart-item');
    if (!cartIsEmpty && cartRows.length > 0) return;

    // Only show fallback if the customer likely came from Buy Again:
    // 1. URL has attributes[from]=new-customer-accounts, OR
    // 2. Referrer is a customer accounts page, OR
    // 3. sessionStorage flag set by a failed /cart/add.js attempt, OR
    // 4. Referrer is empty — Buy Again redirects cross-origin from
    //    account.promotionalproductsonline.com to the store domain, and
    //    the referrer gets stripped in the redirect chain.
    var params = new URLSearchParams(window.location.search);
    var fromAttr = params.get('attributes[from]') || params.get('attributes%5Bfrom%5D') || '';
    var ref = document.referrer || '';
    var fromAccounts = fromAttr === 'new-customer-accounts' ||
                       ref.indexOf('account.') >= 0 ||
                       ref.indexOf('/account') >= 0 ||
                       ref.indexOf('/orders') >= 0 ||
                       sessionStorage.getItem('ppo_buy_again_attempted') === '1' ||
                       ref === '';

    if (!fromAccounts) return;

    // Clean up the flag
    sessionStorage.removeItem('ppo_buy_again_attempted');

    showEmptyCartFallbackNotice();
  }

  function showEmptyCartFallbackNotice() {
    // Insert inside the visible empty-state area, not the outer .cart wrapper
    // (Concept theme only renders content inside .page-width / .empty-state)
    var emptyState = document.querySelector('.cart.is-empty .empty-state');
    var target = emptyState ||
      document.querySelector('.cart.is-empty .page-width') ||
      document.querySelector('cart-items') ||
      document.querySelector('main') ||
      document.body;
    if (!target) return;

    var notice = document.createElement('div');
    notice.id = 'ppo-buy-again-fallback-notice';
    notice.style.cssText = [
      'background:' + ACCENT_BG + ';padding:24px 28px;',
      'margin:20px auto;max-width:640px;',
      'text-align:center;font-family:inherit;',
      'border-radius:10px;border:2px solid ' + ACCENT + ';'
    ].join('');

    var heading = document.createElement('div');
    heading.style.cssText = 'font-size:18px;font-weight:700;color:#000;margin-bottom:10px;';
    heading.textContent = '\uD83D\uDD04 Reordering an older order?';

    var message = document.createElement('div');
    message.style.cssText = 'font-size:14px;color:#444;line-height:1.6;margin-bottom:16px;';
    message.innerHTML =
      'It looks like you tried to reorder a past order, but the items could not be added directly. ' +
      'This is common for orders placed on our <strong>previous website</strong> or for custom items. ' +
      'No problem \u2014 we have your full history on file and can set up your reorder manually.';

    var btn = document.createElement('a');
    btn.href = '/pages/reordering';
    btn.style.cssText = [
      'display:inline-block;background:' + ACCENT + ';color:#fff;text-decoration:none;',
      'font-weight:700;font-size:14px;padding:12px 32px;cursor:pointer;',
      'border-radius:30px;'
    ].join('');
    btn.textContent = 'Request a Reorder';

    var helpText = document.createElement('div');
    helpText.style.cssText = 'font-size:12px;color:#888;margin-top:14px;line-height:1.5;';
    helpText.innerHTML =
      'Or call us at <strong>(877) 228-0656</strong> and mention the order number from your email history.';

    notice.appendChild(heading);
    notice.appendChild(message);
    notice.appendChild(btn);
    notice.appendChild(helpText);

    if (target.firstChild) {
      target.insertBefore(notice, target.firstChild);
    } else {
      target.appendChild(notice);
    }
  }

  function runHandlers() {
    handleReorderFormItems();
    handleStaleReorders();
    handleBuyAgainFailure();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runHandlers);
  } else {
    runHandlers();
  }

  document.addEventListener('shopify:section:load', runHandlers);

  // Qty change interceptor — Concept theme uses "quantity__input"
  document.addEventListener('change', function (e) {
    var input = e.target;

    if (!input.classList.contains('quantity__input')) return;

    var row = input.closest('.cart-item') || input.closest('tr');
    if (!row) return;

    if (row.dataset.hasTiers !== 'true') return;

    var initialQty = parseInt(input.dataset.initialValue || input.defaultValue, 10);
    var newQty     = parseInt(input.value, 10);

    if (newQty === 0 || newQty === initialQty) return;

    e.stopImmediatePropagation();

    input.value = initialQty;
    var qtyEl = input.closest('quantity-input');
    if (qtyEl) qtyEl.currentQty = String(initialQty);

    var titleLink = row.querySelector('a.cart__item-title[href], a[href*="/products/"]');
    if (!titleLink) return;

    showRedirectBanner(titleLink.href);

  }, true /* capture */);

}());
